import type {
  CmmdProject,
  CmmdProjectUpdateStatus,
  CmmdProjectUpdateView,
} from "@cmmd-forge/contracts";

import { ProjectDescriptionEditor } from "./ProjectDescriptionEditor";
import { ProjectUpdatesSection } from "./CmmdProjectOverviewUpdates";
import { OVERVIEW_SECTION_HEADING_CLASS } from "./overviewSectionHeading";

export interface CmmdProjectOverviewTabProps {
  readonly project: CmmdProject;
  readonly updates: readonly CmmdProjectUpdateView[];
  readonly updatesTotal: number;
  readonly updatesLoading: boolean;
  readonly updatesError: string | null;
  readonly onUpdateDescription: (description: string) => Promise<void>;
  readonly onAddUpdate: (input: {
    readonly status: CmmdProjectUpdateStatus;
    readonly body: string;
  }) => Promise<void>;
  readonly onUpdateUpdate: (
    updateId: string,
    input: { readonly status: CmmdProjectUpdateStatus; readonly body: string },
  ) => Promise<void>;
  readonly onDeleteUpdate: (updateId: string) => Promise<void>;
  readonly onViewAllUpdates: () => void;
}

/**
 * Overview main column: latest signal first, short About.
 * Project properties live in the Settings tab.
 */
export function CmmdProjectOverviewTab(props: CmmdProjectOverviewTabProps) {
  const { project } = props;
  // Same width as the updates tab beside it: these are two tabs of one page and
  // both render `ProjectUpdatesSection`, so a narrower column here made the
  // content re-flow under the reader as they switched tabs.
  return (
    <div className="mx-auto w-full max-w-3xl space-y-6 px-5 py-6 sm:px-8">
      <ProjectUpdatesSection
        projectId={project.id}
        title="Latest update"
        density="overview"
        updates={props.updates.slice(0, 1)}
        loading={props.updatesLoading}
        error={props.updatesError}
        onAdd={props.onAddUpdate}
        onUpdate={props.onUpdateUpdate}
        onDelete={props.onDeleteUpdate}
      />
      {props.updatesTotal > 1 ? (
        <button
          type="button"
          onClick={props.onViewAllUpdates}
          className="-mt-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          View all {props.updatesTotal} updates
        </button>
      ) : null}

      <section>
        <h2 className={`mb-1.5 ${OVERVIEW_SECTION_HEADING_CLASS}`}>About</h2>
        <ProjectDescriptionEditor
          projectId={project.id}
          description={project.description}
          previewLines={3}
          onSave={props.onUpdateDescription}
        />
      </section>
    </div>
  );
}
